import { useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { submitFeedback } from "@/lib/api";

export default function FeedbackPanel({ projectId, existing, onSubmitted }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(existing?.comment || "");
  const [email, setEmail] = useState(existing?.email || "");
  const [saving, setSaving] = useState(false);

  const submitted = !!existing;

  const send = async () => {
    if (rating < 1) {
      toast.error("Merci de choisir une note");
      return;
    }
    setSaving(true);
    try {
      const r = await submitFeedback(projectId, { rating, comment, email });
      toast.success("Merci pour votre retour !");
      onSubmitted?.(r.feedback);
    } catch (e) {
      toast.error("Impossible d'envoyer le retour");
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="feedback-submitted"
        className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3"
      >
        <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />
        <div className="text-sm text-emerald-900">
          <span className="font-semibold">Merci pour votre retour</span> — note {existing.rating}/5
          {existing.comment ? ` · « ${existing.comment.slice(0, 100)}${existing.comment.length > 100 ? "…" : ""} »` : ""}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="feedback-panel"
      className="rounded-xl border-2 border-dashed border-[#F59E0B]/60 bg-[#F59E0B]/5 p-5"
    >
      <div className="font-display font-semibold text-[#0F2137]">
        Comment s'est passée la génération ?
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Votre retour aide à améliorer le service. C'est le seul « paiement » demandé pendant la beta.
      </p>

      <div className="mt-4 flex items-center gap-1" data-testid="stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`star-${n}`}
            onClick={() => setRating(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-7 h-7 ${
                (hover || rating) >= n
                  ? "fill-[#F59E0B] text-[#F59E0B]"
                  : "text-slate-300"
              }`}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 font-mono-tech text-sm text-slate-500">{rating}/5</span>
        )}
      </div>

      <Textarea
        data-testid="feedback-comment"
        placeholder="Ce qui vous a plu, ce qui manque, bugs éventuels..."
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="mt-4 bg-white"
        rows={3}
      />

      <Input
        data-testid="feedback-email"
        type="email"
        placeholder="Votre email (optionnel, pour vous tenir au courant)"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mt-3 bg-white"
      />

      <Button
        onClick={send}
        disabled={saving || rating < 1}
        data-testid="btn-submit-feedback"
        className="mt-4 bg-[#0F2137] hover:bg-[#1E293B] text-white"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Envoi...
          </>
        ) : (
          "Envoyer mon retour"
        )}
      </Button>
    </div>
  );
}
